import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  TrendingUp, 
  CheckCircle2, 
  AlertCircle, 
  DollarSign,
  Target,
  Loader2
} from 'lucide-react';

/**
 * Format currency
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Summary Stat Card
 */
const StatCard = ({ title, value, subtitle, icon: Icon, color = "text-primary" }) => {
  return (
    <div className="flex items-start gap-3">
      <div className={`p-2 rounded-lg bg-primary/10 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
};

/**
 * Optimize Summary Component
 */
const OptimizeSummary = ({ summary, onApplyAll, loading }) => {
  const [applying, setApplying] = useState(false);

  if (!summary) {
    return null;
  }

  const handleApplyAll = async () => {
    if (!window.confirm(
      `Apply all AI recommendations?\n\nThis will update ${summary.manual_days} prices.`
    )) {
      return;
    }

    setApplying(true);
    try {
      await onApplyAll();
    } finally {
      setApplying(false);
    }
  };

  const hasManualOverrides = summary.manual_days > 0;
  const potentialGain = summary.potential_revenue || 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Summary ({summary.total_days} Days)</CardTitle>
          {hasManualOverrides && (
            <Button
              onClick={handleApplyAll}
              disabled={applying || loading}
              size="sm"
            >
              {applying ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  Apply All AI Recommendations
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* AI Following */}
          <StatCard
            title="Following AI"
            value={`${summary.ai_days} days`}
            subtitle={`${summary.ai_percent}% adoption`}
            icon={CheckCircle2}
            color="text-green-600"
          />

          {/* Manual Overrides */}
          <StatCard
            title="Manual Override"
            value={`${summary.manual_days} days`}
            subtitle={`${summary.manual_percent}% of total`}
            icon={AlertCircle}
            color="text-yellow-600"
          />

          {/* Average Prices */}
          <StatCard
            title="Avg AI Price"
            value={formatCurrency(summary.avg_ai_price || 0)}
            subtitle={`Current: ${formatCurrency(summary.avg_current_price || 0)}`}
            icon={DollarSign}
            color="text-blue-600"
          />

          {/* Potential Revenue */}
          <StatCard
            title="Potential Gain"
            value={formatCurrency(Math.abs(potentialGain))}
            subtitle={
              potentialGain > 0 
                ? `+${((potentialGain / (summary.avg_current_price * summary.total_days)) * 100).toFixed(1)}% increase`
                : potentialGain < 0
                ? `${((potentialGain / (summary.avg_current_price * summary.total_days)) * 100).toFixed(1)}% decrease`
                : 'No change'
            }
            icon={TrendingUp}
            color={potentialGain > 0 ? 'text-green-600' : potentialGain < 0 ? 'text-red-600' : 'text-gray-600'}
          />
        </div>

        {/* Demand Distribution */}
        {(summary.high_demand_days || summary.medium_demand_days || summary.low_demand_days) && (
          <div className="mt-6 pt-6 border-t">
            <h4 className="text-sm font-medium mb-3">Demand Distribution</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">High</span>
                    <span className="text-sm font-medium">{summary.high_demand_days || 0}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-red-500"
                      style={{ 
                        width: `${((summary.high_demand_days || 0) / summary.total_days) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Medium</span>
                    <span className="text-sm font-medium">{summary.medium_demand_days || 0}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500"
                      style={{ 
                        width: `${((summary.medium_demand_days || 0) / summary.total_days) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-muted-foreground">Low</span>
                    <span className="text-sm font-medium">{summary.low_demand_days || 0}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-500"
                      style={{ 
                        width: `${((summary.low_demand_days || 0) / summary.total_days) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confidence Score */}
        {summary.avg_confidence && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Average ML Confidence</span>
              <span className="text-sm font-medium">
                {(summary.avg_confidence * 100).toFixed(0)}%
              </span>
            </div>
            <div className="mt-2 h-2 bg-secondary rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary"
                style={{ width: `${summary.avg_confidence * 100}%` }}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OptimizeSummary;
